import { renewablePlan } from 'constants/subscription';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useSetPageTitle from 'utilities/useSetPageTitle';
import './LandingPage.scss';
import { useServices } from 'index';
import sectionOneImg from 'images/LandingPage/SectionOne.png';
import sectionTwoImg from 'images/LandingPage/SectionTwo.png';
import sectionThreeImg from 'images/LandingPage/SectionThree.png';
import { ReactComponent as TMLogo } from '../../images/MainMenu/Trackman1.svg';
import { Button } from '@trackman/web-shared-components';
import { pathLogin } from 'constants/authenticationPaths';
import SectionTitle from 'components/TextComps/SectionTitle';
import Subtitle from 'components/TextComps/Subtitle';
import { ReactComponent as Crown } from 'images/icons/Crown.svg';
import { ReactComponent as Checkmark } from 'images/icons/Checkmark.svg';
import { ReactComponent as Close } from 'images/icons/CloseRegular.svg';
import { isLandingPageDetailsEnabled } from 'utilities/enabledFeaturesHelper';
import { useGetSubscriptionPrice } from 'queryHooks/subscriptionPrice';

export const LandingPage = () => {
  const { t } = useTranslation();
  const configurationService = useServices().configurationService;

  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const logIn = () => {
    setIsLoggingIn(true);
    window.location.assign(`${configurationService.getWindowOrigin()}/${pathLogin}`);
  };

  useSetPageTitle({
    title: t('TrackmanUniversityFreeGolfEducation', { ns: 'general' }),
  });

  return (
    <div className='landing-page'>
      <div className='landing-page-hero'>
        <div className='landing-page-hero-content'>
          <TMLogo />
          <div>
            <h1 className='hero-title'>{t('UnleashPotential', { ns: 'landingPage' })}</h1>
            <div className='hero-subtitle'>{t('CompleteYourGolfKnowledge', { ns: 'landingPage' })}</div>
            <Button
              label={t('LogIn', { ns: 'landingPage' })}
              intent='primary'
              onClick={() => logIn()}
              className='login-button styled-button primary'
              disabled={isLoggingIn}
            />
          </div>
        </div>
      </div>
      {isLandingPageDetailsEnabled && (
        <>
          <div className='landing-page-sections'>
            <div className='landing-page-section'>
              <img src={sectionOneImg} alt='Section One' />
              <div>
                <SectionTitle>{t('UnderstandEveryNumber', { ns: 'landingPage' })}</SectionTitle>
                <p>{t('EveryTrackmanDataPoint', { ns: 'landingPage' })}</p>
              </div>
            </div>
            <div className='landing-page-section'>
              <img src={sectionTwoImg} alt='Section Two' />
              <div>
                <SectionTitle>{t('LearnByDoing', { ns: 'landingPage' })}</SectionTitle>
                <p>{t('InteractiveCalculators', { ns: 'landingPage' })}</p>
              </div>
            </div>
            <div className='landing-page-section'>
              <img src={sectionThreeImg} alt='Section Three' />
              <div>
                <SectionTitle>{t('EarnCertificationsThatLastALifetime', { ns: 'landingPage' })}</SectionTitle>
                <p>{t('ProveYourKnowledge', { ns: 'landingPage' })}</p>
              </div>
            </div>
          </div>
          <div className='landing-page-plan-section'>
            <Subtitle>{t('StartFreeOfCharge', { ns: 'landingPage' })}</Subtitle>
            <div className='plan-cards'>
              <FreePlanCard />
              <PremiumPlanCard />
            </div>
            <div>
              <span>{t('AlreadyATrackmanSoftwareUser', { ns: 'landingPage' })}</span>
              <span>{t('BallFlightLevel1IsAPrerequisite', { ns: 'landingPage' })}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const BenefitBulletPoint = ({ included, isPremium }: { included: boolean; isPremium?: boolean }) => {
  const bulletPointClasses = `benefit-bullet-point ${isPremium ? 'benefit-bullet-point__premium' : !included ? 'benefit-bullet-point__not-included' : ''}`;
  return <div className={bulletPointClasses}>{included ? <Checkmark /> : <Close />}</div>;
};

const FreePlanCard = () => {
  const { t } = useTranslation();

  return (
    <div className='plan-card'>
      <Subtitle>{t('Basic', { ns: 'webshop' })}</Subtitle>
      <h2>{t('Free', { ns: 'webshop' })}</h2>
      <p>{t('StartJourney', { ns: 'landingPage' })}</p>
      <div className='benefits-list'>
        <div className='benefit-item'>
          <BenefitBulletPoint included={true} />
          {t('LearningHubAndParameters', { ns: 'landingPage' })}
        </div>
        <div className='benefit-item'>
          <BenefitBulletPoint included={true} />
          {t('AccessToParameters', { ns: 'landingPage' })}
        </div>
        <div className='benefit-item'>
          <BenefitBulletPoint included={true} />
          {t('AccessToFreeWebinars', { ns: 'landingPage' })}
        </div>
        <div className='benefit-item'>
          <BenefitBulletPoint included={true} />
          {t('CertificationsAtFullPrice', { ns: 'landingPage' })}
        </div>
        <div className='benefit-item'>
          <BenefitBulletPoint included={false} />
          {t('AdaptiveChapterTests', { ns: 'landingPage' })}
        </div>
        <div className='benefit-item'>
          <BenefitBulletPoint included={false} />
          {t('PremiumWebinars', { ns: 'landingPage' })}
        </div>
        <div className='benefit-item'>
          <BenefitBulletPoint included={false} />
          {t('InPersonWorkshops', { ns: 'landingPage' })}
        </div>
      </div>
    </div>
  );
};

const PremiumPlanCard = () => {
  const { t } = useTranslation();
  const { data: subscriptionPrice } = useGetSubscriptionPrice(renewablePlan);

  return (
    <div className='plan-card'>
      <Subtitle>
        <>
          <div className='premium-plan-header'>
            <Crown className='premium-plan-icon' />
            {t('Premium', { ns: 'webshop' })}
          </div>
        </>
      </Subtitle>
      <h2>
        <Trans
          i18nKey='PriceWithYear'
          ns='webshop'
          defaults={`<0>{{price}}</0><1>/year</1>`}
          values={{ price: subscriptionPrice }}
          components={[<span key='0' />, <span key='1' className='' />]}
        />
      </h2>
      <p>{t('EverythingYouNeedToUnderstand', { ns: 'landingPage' })}</p>
      <div className='benefits-list'>
        <span>{t('InAdditionToAllFreeFeatures', { ns: 'landingPage' })}</span>
        <div className='benefit-item'>
          <BenefitBulletPoint included={true} isPremium={true} />
          {t('AdaptiveChapterTests', { ns: 'landingPage' })}
        </div>
        <div className='benefit-item'>
          <BenefitBulletPoint included={true} isPremium={true} />
          {t('AccessToPremiumContent', { ns: 'landingPage' })}
        </div>
        <div className='benefit-item'>
          <BenefitBulletPoint included={true} isPremium={true} />
          {t('50%DiscountOnAllCertifications', { ns: 'landingPage' })}
        </div>
        <div className='benefit-item'>
          <BenefitBulletPoint included={true} isPremium={true} />
          {t('AccessToInPersonWorkshops', { ns: 'landingPage' })}
        </div>
      </div>
    </div>
  );
};
